import { AfterViewInit, Directive, ElementRef, forwardRef, inject, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { KH_A11Y_TARGET } from '../constants/a11y-target.token';

import { A11yLabelDirective } from './a11y-label.directive';

const DEFAULT_IFRAME_TITLE_KEY = 'A11Y_STRIPE_ELEMENT.IFRAME_TITLE';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]'
].join(', ');

@Directive({
  selector:
    'ngx-stripe-card[khA11yStripeElement],' +
    'ngx-stripe-card-number[khA11yStripeElement],' +
    'ngx-stripe-card-expiry[khA11yStripeElement],' +
    'ngx-stripe-card-cvc[khA11yStripeElement]',
  providers: [{ provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11yStripeElementDirective) }]
})
export class A11yStripeElementDirective extends A11yLabelDirective implements AfterViewInit, OnChanges, OnDestroy {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _translationService = inject(TranslationService);

  private _mutationObserver?: MutationObserver;
  private _viewInitialized = false;
  private readonly _titledIframes = new WeakSet<HTMLIFrameElement>();

  /** Optional override for the untitled-iframe title. Defaults to the shared Stripe frame title. */
  @Input() public khA11yStripeElementIframeTitleKey: string | null = null;

  public constructor() {
    super();
    this._translationService.translationsReady$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.patchStripeAccessibility());
  }

  public ngAfterViewInit(): void {
    this._viewInitialized = true;
    this.patchStripeAccessibility();
    this._mutationObserver = new MutationObserver(() => this.patchStripeAccessibility());
    this._mutationObserver.observe(this._elementRef.nativeElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'tabindex']
    });
  }

  public ngOnChanges(_changes: SimpleChanges): void {
    this.patchStripeAccessibility();
  }

  public ngOnDestroy(): void {
    this._mutationObserver?.disconnect();
  }

  private patchStripeAccessibility(): void {
    if (!this._viewInitialized) {
      return;
    }

    const host = this._elementRef.nativeElement;

    host.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR).forEach((element) => {
      if (element.tagName === 'IFRAME') {
        // Stripe marks field iframes tabindex="-1" for its own focus management.
        // Leaving that in place dumps Tab out of the iframe to the document start.
        if (element.getAttribute('tabindex') !== '0') {
          this._renderer.setAttribute(element, 'tabindex', '0');
        }
        return;
      }

      if (element.closest('[aria-hidden="true"]') && element.getAttribute('tabindex') !== '-1') {
        this._renderer.setAttribute(element, 'tabindex', '-1');
      }
    });

    const iframeTitleKey = this.khA11yStripeElementIframeTitleKey || DEFAULT_IFRAME_TITLE_KEY;
    const iframeTitle = this._translationService.instant(iframeTitleKey);

    if (!iframeTitle || iframeTitle === iframeTitleKey) {
      return;
    }

    host.querySelectorAll<HTMLIFrameElement>('iframe').forEach((iframe) => {
      const existingTitle = iframe.getAttribute('title')?.trim();
      if (existingTitle && !this._titledIframes.has(iframe)) {
        return;
      }

      this._renderer.setAttribute(iframe, 'title', iframeTitle);
      this._titledIframes.add(iframe);
    });
  }
}
